import React from "react";
import { RC_ICON } from "../lib/constants";
import { MapPin, Globe } from "lucide-react";
import { useLang, SPANISH_ENABLED } from "../lib/i18n";

export const LanguageToggle = ({ variant = "light" }) => {
  const { lang, setLang } = useLang();
  if (!SPANISH_ENABLED) return null;
  const onLight = variant === "light";
  const base = "px-2.5 py-1 text-xs font-bold rounded-full transition-colors duration-200";
  const activeCls = onLight ? "bg-vin text-white" : "bg-white text-vin";
  const idleCls = onLight ? "text-ink-soft hover:text-vin" : "text-white/80 hover:text-white";
  return (
    <div
      data-testid="language-toggle"
      className={`inline-flex items-center gap-1 rounded-full px-1.5 py-1 ${onLight ? "bg-slate-100" : "bg-white/10"}`}
    >
      <Globe className={`w-3.5 h-3.5 ml-0.5 hidden sm:inline-block ${onLight ? "text-ink-soft" : "text-white/70"}`} strokeWidth={2} aria-hidden="true" />
      <button
        data-testid="lang-en-button"
        onClick={() => setLang("en")}
        className={`${base} ${lang === "en" ? activeCls : idleCls}`}
        aria-pressed={lang === "en"}
      >
        EN
      </button>
      <button
        data-testid="lang-es-button"
        onClick={() => setLang("es")}
        className={`${base} ${lang === "es" ? activeCls : idleCls}`}
        aria-pressed={lang === "es"}
      >
        ES
      </button>
    </div>
  );
};

export const AppHeader = ({ subtitle }) => {
  const { t } = useLang();
  return (
    <header className="sticky top-0 z-20 bg-white border-b border-[var(--line)]">
      <div className="max-w-5xl mx-auto px-5 sm:px-6 h-16 flex items-center gap-2 sm:gap-3">
        <img src={RC_ICON} alt="Root Causes Challenge" className="h-9 w-9 sm:h-10 sm:w-10 object-contain rounded-md shrink-0" />
        <div className="leading-tight min-w-0 flex-1">
          <div className="font-display font-semibold text-vin text-sm sm:text-base truncate">
            <span className="sm:hidden">{t("Root Causes")}</span>
            <span className="hidden sm:inline">{t("Root Causes Challenge")}</span>
          </div>
          {subtitle && <div className="text-[11px] text-ink-soft truncate">{subtitle}</div>}
        </div>
        <nav className="ml-auto flex items-center gap-0.5 sm:gap-2 shrink-0">
          <a
            href="/voices"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-voices-tab"
            className="px-2 sm:px-3 py-1.5 rounded-full text-[12px] sm:text-sm font-semibold text-vin hover:bg-vin/5 transition-colors duration-200 whitespace-nowrap"
          >
            <span className="sm:hidden">{t("Pathways")}</span>
            <span className="hidden sm:inline">{t("Policy Pathways")}</span>
          </a>
          <a
            href="/lessons"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-lessons-tab"
            className="px-2 sm:px-3 py-1.5 rounded-full text-[12px] sm:text-sm font-semibold text-vin hover:bg-vin/5 transition-colors duration-200 whitespace-nowrap"
          >
            <span className="sm:hidden">{t("Lessons")}</span>
            <span className="hidden sm:inline">{t("Lessons Learned")}</span>
          </a>
          <LanguageToggle variant="light" />
        </nav>
      </div>
    </header>
  );
};

export const CommunitySelection = ({ communities, onSelect, selecting, quick = false }) => {
  const { t } = useLang();
  return (
    <div className="min-h-screen">
      <AppHeader subtitle={t("An interactive poverty simulation")} />
      <div className="max-w-5xl mx-auto px-5 sm:px-6 py-10 sm:py-14">
        <div className="max-w-2xl animate-rise">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-vin/70 mb-3">
            {t("Step 1 · Choose a community")}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-ink leading-tight">
            {t("Where does your family live?")}
          </h2>
          <p className="mt-4 text-base text-ink-soft leading-relaxed">
            {t("Each community below is inspired by publicly available data about real places across the United States. The place you choose will quietly shape the")}
            {quick ? t(" situations your family faces.") : t(" choices your family faces this year.")}
          </p>
        </div>

        <div className="mt-9 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {communities.map((c, i) => (
            <button
              key={c.id}
              data-testid={`community-card-${c.id}`}
              onClick={() => onSelect(c.id)}
              disabled={selecting}
              className="group text-left bg-white rounded-lg border border-[var(--line)] p-6 hover:border-vin hover:shadow-md transition-all duration-200 disabled:opacity-60"
            >
              <div className="flex items-center gap-2 text-vin mb-3">
                <MapPin className="w-4 h-4" strokeWidth={1.75} />
                <span className="text-[11px] uppercase tracking-[0.14em] font-semibold">
                  {c.region}
                </span>
              </div>
              <h3 className="font-display text-lg font-semibold text-ink group-hover:text-vin transition-colors duration-200">
                {c.name}
              </h3>
              <p className="mt-2 text-sm text-ink-soft leading-relaxed">{c.desc}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CommunitySelection;
